import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { PASSWORD_MIN_LENGTH } from '@gk/contracts'
import { authClient, type AuthClient, type Factor } from '../auth/client.ts'
import { Banner, Field, PasswordInput } from '../ui/components.tsx'

/** The two one-time links an administrator hands out (the token rides in the URL FRAGMENT, erased from the address bar at once): a person's first sign-in
 *  (or a new link after a lost password), where they choose a password, and a workstation's enrolment, which only signs the workstation in (M22, docs/STACK.md
 *  9.5: the server's auth module). Consuming the link first signs out whatever session this browser held (another person's, on a shared office PC).
 *  A person whose login has a verified authenticator gives its code BEFORE the password (the server refuses a password change below aal2: a link
 *  issued by someone else cannot take the account over); one who lost the authenticator asks the Superintendent, who deletes the factor first. */
export function AcceptPage({ mode, client = authClient, leave = (path) => window.location.replace(path) }: { mode: 'accept' | 'kiosk'; client?: AuthClient; leave?: (path: string) => void }) {
  const { t } = useTranslation()
  const [state, setState] = useState<'checking' | 'code' | 'password' | 'failed' | 'done'>('checking')
  const [factor, setFactor] = useState<Factor | null>(null)
  const [code, setCode] = useState('')
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return                                          // the link is used once, even when an effect runs twice
    started.current = true
    const p = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const token = p.get('t')
    if (!token) { setState('failed'); return }
    window.history.replaceState(null, '', window.location.pathname)      // the token leaves the address bar at once
    void client.consumeLink(token).then((r) => {
      if ('error' in r) { setState('failed'); return }
      if (r.linkKind === 'kiosk') { setState('done'); window.setTimeout(() => leave('/'), 800); return }
      const verified = r.user.factors.find((f) => f.status === 'verified') ?? null
      setFactor(verified)
      setState(verified ? 'code' : 'password')
    })
  }, [client, leave])

  async function verify(e: FormEvent) {
    e.preventDefault(); if (!factor) return
    setBusy(true); setError(null)
    const r = await client.verifyFactor(factor.id, code.replace(/\s/g, ''))
    setBusy(false)
    if (r.error) { setError(r.error === 'network' || r.error === 'timeout' ? t('errors.network') : t('auth.mfaInvalid')); return }
    setState('password')
  }

  async function save(e: FormEvent) {
    e.preventDefault(); setError(null)
    if (pw.length < PASSWORD_MIN_LENGTH) { setError(t('accept.tooShort', { min: PASSWORD_MIN_LENGTH })); return }
    if (pw !== pw2) { setError(t('accept.mismatch')); return }
    setBusy(true)
    const r = await client.setPassword(pw)
    setBusy(false)
    if (r.error) {
      if (r.error === 'weak_password') setError(t('accept.tooShort', { min: PASSWORD_MIN_LENGTH }))
      else if (r.error === 'aal2_required' && factor) { setState('code'); setError(t('auth.mfaInvalid')) }
      else setError(r.error === 'network' || r.error === 'timeout' ? t('errors.network') : t('errors.unknown'))
      return
    }
    leave('/')
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-6" data-testid="accept-page" data-state={state}>
      <h1 className="text-2xl font-bold">{mode === 'kiosk' ? t('accept.kioskTitle') : t('accept.title')}</h1>
      {state === 'checking' && <p>{t('common.loading')}</p>}
      {state === 'failed' && <Banner tone="error">{t('accept.failed')}</Banner>}
      {state === 'done' && <Banner tone="ok">{t('accept.kioskDone')}</Banner>}
      {state === 'code' && (
        <form onSubmit={(e) => void verify(e)} className="flex flex-col gap-4" data-testid="accept-code-form">
          <h2 className="text-xl font-bold">{t('auth.mfaTitle')}</h2>
          <p>{t('auth.mfaHelp')}</p>
          <Field label={t('auth.mfaCode')} htmlFor="accept-code">
            <input id="accept-code" className="input text-center text-2xl tracking-widest" inputMode="numeric" autoComplete="one-time-code" maxLength={7} required value={code} onChange={(e) => setCode(e.target.value)} data-testid="accept-code" />
          </Field>
          {error && <Banner tone="error">{error}</Banner>}
          <button className="btn btn-primary" disabled={busy} data-testid="accept-code-go">{t('auth.mfaVerify')}</button>
          <p className="text-sm opacity-75" data-testid="accept-lost-factor">{t('accept.lostFactor')}</p>
        </form>
      )}
      {state === 'password' && (
        <form onSubmit={(e) => void save(e)} className="flex flex-col gap-4">
          <p>{t('accept.help')}</p>
          <Field label={t('accept.password')} hint={t('accept.passwordHelp', { min: PASSWORD_MIN_LENGTH })} htmlFor="pw"><PasswordInput id="pw" autoComplete="new-password" value={pw} onChange={setPw} testId="accept-password" /></Field>
          <Field label={t('accept.again')} htmlFor="pw2"><PasswordInput id="pw2" autoComplete="new-password" value={pw2} onChange={setPw2} testId="accept-password2" /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          <button className="btn btn-primary" disabled={busy} data-testid="accept-save">{t('accept.save')}</button>
        </form>
      )}
    </main>
  )
}
